import React from 'react';
import { X, ShoppingBag } from 'lucide-react';

interface CardOrder {
  id: string;
  lob_order_id: string;
  status: string;
  expected_delivery_date: string;
  credits_used: number;
  recipient_name: string;
  recipient_address: string;
  created_at: string;
  contact_id?: string;
  card_id?: string;
  message?: string;
}

interface OrderHistoryModalProps {
  orderHistory: CardOrder[];
  orderHistoryLoading: boolean;
  orderHistoryPage: number;
  orderHistoryTotal: number;
  ORDER_HISTORY_LIMIT: number;
  onClose: () => void;
  onPageChange: (page: number) => void;
  onSendFirstCard: () => void;
}

// Add a function to format the date
function formatDate(dateString: string): string {
  if (!dateString) return 'N/A';
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
}

export function OrderHistoryModal({ 
  orderHistory, 
  orderHistoryLoading, 
  orderHistoryPage, 
  orderHistoryTotal, 
  ORDER_HISTORY_LIMIT,
  onClose,
  onPageChange,
  onSendFirstCard
}: OrderHistoryModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold font-display text-brand-secondary flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Order History
          </h2>
          <button
            onClick={onClose}
            className="text-brand-secondary hover:text-brand-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-brand-secondary mb-6">
            View your past orders and credit usage history.
          </p>

          {orderHistoryLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading order history...</p>
            </div>
          ) : orderHistory.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">You haven't sent any cards yet.</p>
              <button
                onClick={onSendFirstCard}
                className="mt-4 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
              >
                Send Your First Card
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expected Delivery
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Credits Used
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orderHistory.map((order) => (
                      <tr key={order.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{order.recipient_name}</div>
                          <div className="text-sm text-gray-500 truncate max-w-[200px]">{order.recipient_address}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            order.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.expected_delivery_date ? formatDate(order.expected_delivery_date) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.credits_used}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {orderHistoryTotal > ORDER_HISTORY_LIMIT && (
                <div className="flex justify-between items-center mt-6">
                  <div className="text-sm text-gray-500">
                    Showing {orderHistoryPage * ORDER_HISTORY_LIMIT + 1} to {Math.min((orderHistoryPage + 1) * ORDER_HISTORY_LIMIT, orderHistoryTotal)} of {orderHistoryTotal} orders
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onPageChange(Math.max(0, orderHistoryPage - 1))}
                      disabled={orderHistoryPage === 0}
                      className="px-3 py-1 bg-gray-100 text-brand-secondary rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => onPageChange(orderHistoryPage + 1)}
                      disabled={(orderHistoryPage + 1) * ORDER_HISTORY_LIMIT >= orderHistoryTotal}
                      className="px-3 py-1 bg-gray-100 text-brand-secondary rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
} 